import React, { useState, useCallback, useEffect } from 'react';
import { RefreshCw, BarChart3, TrendingUp, DollarSign, Target, Clock, Users, Award } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

interface Position {
  id: string;
  symbol: string;
  timestamp: string;
  entryPrice: number;
  exitPrice?: number;
  quantity: number;
  side: 'BUY' | 'SELL';
  status: 'OPEN' | 'CLOSED';
  pnl?: number;
  pnlPercent?: number;
  aiProvider: string;
  aiModel: string;
  confidence: number;
  exitConfidence?: number;
  duration?: string;
  oneHourAccuracy?: number;
  oneDayAccuracy?: number;
}

interface PerformanceStats {
  totalPositions: number;
  openPositions: number;
  closedPositions: number;
  totalPnl: number;
  winRate: number;
  avgDuration: string;
  avgAccuracy: number;
}

const AIPredictionPerformance: React.FC = () => {
  const { theme } = useTheme();
  const [positions, setPositions] = useState<Position[]>([]);
  const [stats, setStats] = useState<PerformanceStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [activeFilter, setActiveFilter] = useState('전체');

  // 숫자 포맷팅 함수
  const formatNumber = (num: number, decimals: number = 2): string => {
    return num.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };

  // 달러 포맷팅 함수 (소수점 둘째자리까지)
  const formatCurrency = (amount: number): string => {
    if (amount >= 0) {
      return `$${formatNumber(amount, 2)}`;
    } else {
      return `-$${formatNumber(Math.abs(amount), 2)}`;
    }
  };

  // 목업 데이터 생성 (28% 수익률 달성하도록 조정)
  const generateMockData = useCallback(() => {
    // 암호화폐 심볼 풀
    const symbols = [
      'BTC/USDT', 'ETH/USDT', 'BNB/USDT', 'SOL/USDT', 'ADA/USDT', 
      'DOT/USDT', 'AVAX/USDT', 'MATIC/USDT', 'LINK/USDT', 'UNI/USDT',
      'XRP/USDT', 'LTC/USDT', 'BCH/USDT', 'ATOM/USDT', 'ALGO/USDT',
      'VET/USDT', 'FIL/USDT', 'THETA/USDT', 'AAVE/USDT', 'COMP/USDT',
      'NEAR/USDT', 'SAND/USDT', 'MANA/USDT', 'CRV/USDT', 'SUSHI/USDT'
    ];

    const aiProviders = ['GPT-4', 'Claude-3', 'Gemini Pro', 'Technical'];
    const aiModels = ['GPT-4', 'Claude-3', 'Gemini Pro'];

    // 랜덤 함수들
    const randomChoice = (arr: any[]) => arr[Math.floor(Math.random() * arr.length)];
    const randomFloat = (min: number, max: number) => Math.random() * (max - min) + min;
    const randomInt = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;

    // 날짜 생성 (최근 30일)
    const generateRandomDate = (daysAgo: number) => {
      const now = new Date();
      const date = new Date(now.getTime() - (daysAgo * 24 * 60 * 60 * 1000));
      date.setHours(randomInt(0, 23), randomInt(0, 59), 0, 0);
      return date.toISOString();
    };

    // 기간 포맷팅
    const formatDuration = (hours: number) => {
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      const minutes = randomInt(0, 59);
      return `${days}d ${remainingHours}h ${minutes}m`;
    };

    // 가격 생성 (심볼별 현실적인 가격 범위)
    const generatePrice = (symbol: string) => {
      const priceRanges: { [key: string]: [number, number] } = {
        'BTC/USDT': [95000, 110000],
        'ETH/USDT': [3500, 4200],
        'BNB/USDT': [580, 650],
        'SOL/USDT': [220, 280],
        'ADA/USDT': [0.4, 0.6],
        'DOT/USDT': [6, 9],
        'AVAX/USDT': [35, 50],
        'MATIC/USDT': [0.8, 1.2],
        'LINK/USDT': [13, 18],
        'UNI/USDT': [6, 9],
        'XRP/USDT': [0.5, 0.7],
        'LTC/USDT': [85, 105],
        'BCH/USDT': [220, 280],
        'ATOM/USDT': [7, 11],
        'ALGO/USDT': [0.15, 0.25],
        'VET/USDT': [0.02, 0.04],
        'FIL/USDT': [4, 7],
        'THETA/USDT': [1, 2],
        'AAVE/USDT': [85, 120],
        'COMP/USDT': [40, 60],
        'NEAR/USDT': [2, 4],
        'SAND/USDT': [0.3, 0.6],
        'MANA/USDT': [0.4, 0.8],
        'CRV/USDT': [0.6, 1.2],
        'SUSHI/USDT': [0.8, 1.5]
      };
      
      const [min, max] = priceRanges[symbol] || [1, 100];
      return randomFloat(min, max);
    };

    // 수량 생성 (가격에 반비례하여 현실적인 수량)
    const generateQuantity = (symbol: string, price: number) => {
      const investmentAmount = randomFloat(800, 3000); // $800-$3000 투자
      const baseQuantity = investmentAmount / price;
      
      // 소수점 자리수 조정
      if (price > 1000) return parseFloat(baseQuantity.toFixed(4));
      if (price > 100) return parseFloat(baseQuantity.toFixed(3));
      if (price > 10) return parseFloat(baseQuantity.toFixed(2));
      if (price > 1) return parseFloat(baseQuantity.toFixed(1));
      return Math.floor(baseQuantity);
    };

    const mockPositions: Position[] = [];
    let totalInvestment = 0;
    let totalPnl = 0;

    // 완료된 포지션 생성 (28개)
    for (let i = 0; i < 28; i++) {
      const symbol = randomChoice(symbols);
      const entryPrice = generatePrice(symbol);
      const quantity = generateQuantity(symbol, entryPrice);
      const investment = entryPrice * quantity;
      
      // 28% 수익률을 위한 수익/손실 확률 조정
      // 승률 약 65%, 평균 수익 12%, 평균 손실 -6%
      const isWin = Math.random() < 0.65;
      let pnlPercent: number;
      
      if (isWin) {
        // 승리 시: 3% ~ 25% 수익
        pnlPercent = randomFloat(3, 25);
      } else {
        // 손실 시: -1% ~ -15% 손실
        pnlPercent = -randomFloat(1, 15);
      }

      const exitPrice = entryPrice * (1 + pnlPercent / 100);
      const pnl = (exitPrice - entryPrice) * quantity;
      
      totalInvestment += investment;
      totalPnl += pnl;

      const daysAgo = randomInt(1, 30);
      const duration = randomInt(2, 72); // 2시간 ~ 3일

      mockPositions.push({
        id: (i + 1).toString(),
        symbol,
        timestamp: generateRandomDate(daysAgo),
        entryPrice: parseFloat(entryPrice.toFixed(symbol.includes('USDT') && entryPrice < 1 ? 6 : 2)),
        exitPrice: parseFloat(exitPrice.toFixed(symbol.includes('USDT') && exitPrice < 1 ? 6 : 2)),
        quantity,
        side: 'BUY',
        status: 'CLOSED',
        pnl: parseFloat(pnl.toFixed(2)),
        pnlPercent: parseFloat(pnlPercent.toFixed(2)),
        aiProvider: randomChoice(aiProviders),
        aiModel: randomChoice(aiModels),
        confidence: randomInt(65, 95),
        exitConfidence: randomInt(70, 95),
        duration: formatDuration(duration),
        oneHourAccuracy: randomInt(70, 95),
        oneDayAccuracy: randomInt(75, 92)
      });
    }

    // 현재 수익률 계산 및 조정
    let currentReturnRate = (totalPnl / totalInvestment) * 100;
    console.log(`현재 수익률: ${currentReturnRate.toFixed(2)}%`);

    // 28%에 맞추기 위한 조정
    const targetReturn = 28;
    const adjustment = targetReturn - currentReturnRate;
    
    if (Math.abs(adjustment) > 1) {
      // 마지막 몇 개 포지션의 수익률 조정
      const positionsToAdjust = Math.min(5, mockPositions.length);
      const adjustmentPerPosition = adjustment / positionsToAdjust;
      
      for (let i = mockPositions.length - positionsToAdjust; i < mockPositions.length; i++) {
        const position = mockPositions[i];
        const newPnlPercent = position.pnlPercent! + adjustmentPerPosition;
        const newExitPrice = position.entryPrice * (1 + newPnlPercent / 100);
        const newPnl = (newExitPrice - position.entryPrice) * position.quantity;
        
        position.pnlPercent = parseFloat(newPnlPercent.toFixed(2));
        position.exitPrice = parseFloat(newExitPrice.toFixed(position.symbol.includes('USDT') && newExitPrice < 1 ? 6 : 2));
        position.pnl = parseFloat(newPnl.toFixed(2));
      }
    }

    // 진행중인 포지션 8개 추가
    for (let i = 0; i < 8; i++) {
      const symbol = randomChoice(symbols);
      const entryPrice = generatePrice(symbol);
      const quantity = generateQuantity(symbol, entryPrice);
      const hoursAgo = randomInt(1, 48);

      mockPositions.push({
        id: (29 + i).toString(),
        symbol,
        timestamp: generateRandomDate(hoursAgo / 24),
        entryPrice: parseFloat(entryPrice.toFixed(symbol.includes('USDT') && entryPrice < 1 ? 6 : 2)),
        quantity,
        side: 'BUY',
        status: 'OPEN',
        aiProvider: randomChoice(aiProviders),
        aiModel: randomChoice(aiModels),
        confidence: randomInt(70, 90),
        duration: formatDuration(hoursAgo),
        oneHourAccuracy: randomInt(75, 92),
        oneDayAccuracy: randomInt(78, 90)
      });
    }

    // 날짜순 정렬 (최신순)
    mockPositions.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    // 최종 수익률 계산
    const finalTotalPnl = mockPositions
      .filter(p => p.status === 'CLOSED')
      .reduce((sum, p) => sum + (p.pnl || 0), 0);
    
    const finalTotalInvestment = mockPositions
      .filter(p => p.status === 'CLOSED')
      .reduce((sum, p) => sum + (p.entryPrice * p.quantity), 0);
    
    const finalReturnRate = (finalTotalPnl / finalTotalInvestment) * 100;
    
    console.log(`✅ 최종 수익률: ${finalReturnRate.toFixed(2)}%`);
    console.log(`총 투자금: $${finalTotalInvestment.toFixed(2)}`);
    console.log(`총 수익: $${finalTotalPnl.toFixed(2)}`);

    setPositions(mockPositions);

    // 통계 계산
    const mockStats: PerformanceStats = {
      totalPositions: 36,
      openPositions: 11,
      closedPositions: 25,
      totalPnl: 411.73,
      winRate: 56.0,
      avgDuration: '2d 14h',
      avgAccuracy: 79.8
    };

    setStats(mockStats);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    generateMockData();
  }, [generateMockData]);

  // 필터링된 포지션 가져오기
  const getFilteredPositions = () => {
    switch (activeFilter) {
      case '진행중':
        return positions.filter(p => p.status === 'OPEN');
      case '완료':
        return positions.filter(p => p.status === 'CLOSED');
      default:
        return positions;
    }
  };

  // 페이지네이션
  const getPaginatedPositions = () => {
    const filteredPositions = getFilteredPositions();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredPositions.slice(startIndex, endIndex);
  };

  const filteredPositions = getFilteredPositions();
  const totalPages = Math.ceil(filteredPositions.length / itemsPerPage);

  // 필터 변경 시 페이지 리셋
  const handleFilterChange = (filter: string) => {
    setActiveFilter(filter);
    setCurrentPage(1);
  };

  // 유틸리티 함수들
  const formatDateTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const dateStr = date.toLocaleDateString('ko-KR', {
      month: '2-digit',
      day: '2-digit'
    });
    const timeStr = date.toLocaleTimeString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    return { date: dateStr, time: timeStr };
  };

  const getPnlColor = (pnl?: number) => {
    if (!pnl) return theme === 'dark' ? 'text-gray-400' : 'text-gray-500';
    return pnl > 0 ? 'text-green-500' : 'text-red-500';
  };

  const getAccuracyColor = (accuracy?: number) => {
    if (!accuracy) return theme === 'dark' ? 'text-gray-400' : 'text-gray-500';
    if (accuracy >= 80) return 'text-green-500';
    if (accuracy >= 60) return 'text-yellow-500';
    return 'text-red-500';
  };

  if (isLoading) {
    return (
      <div className={`h-full flex items-center justify-center ${theme === 'dark' ? 'bg-slate-800 text-white' : 'bg-white text-gray-900'}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className={`h-full flex flex-col ${theme === 'dark' ? 'bg-slate-800 text-white' : 'bg-white text-gray-900'}`}>
      {/* 헤더 */}
      <div className={`flex items-center justify-between p-4 border-b ${theme === 'dark' ? 'border-slate-700' : 'border-gray-200'}`}>
          <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
            <BarChart3 className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className={`text-lg font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>포지션 거래 내역</h2>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>AI 기반 실시간 트레이딩 성과</p>
          </div>
          </div>
          <button
          onClick={generateMockData}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm ${
            theme === 'dark' 
              ? 'bg-slate-700 hover:bg-slate-600 text-white' 
              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
          }`}
          >
            <RefreshCw className="h-4 w-4" />
          </button>
      </div>

      {/* 통계 카드 - 3개씩 2줄 */}
      {stats && (
        <div className={`p-4 border-b ${theme === 'dark' ? 'border-slate-700' : 'border-gray-200'}`}>
          <div className="grid grid-cols-3 gap-4 mb-4">
            {/* 첫 번째 줄 */}
            {/* 총 포지션 */}
            <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="h-4 w-4 text-blue-400" />
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>총 포지션</span>
              </div>
              <div className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{stats.totalPositions}</div>
            </div>

            {/* 진행중 */}
            <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-4 w-4 text-green-400" />
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>진행중</span>
              </div>
              <div className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{stats.openPositions}</div>
            </div>

            {/* 승률 */}
            <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <Award className="h-4 w-4 text-purple-400" />
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>승률</span>
              </div>
              <div className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{stats.winRate}%</div>
            </div>

            {/* 두 번째 줄 */}
            {/* 총 손익 */}
            <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="h-4 w-4 text-green-400" />
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>총 손익</span>
              </div>
              <div className="text-2xl font-bold text-green-400">{formatCurrency(stats.totalPnl)}</div>
          </div>

            {/* 평균 보유 */}
            <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-4 w-4 text-gray-400" />
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>평균 보유</span>
          </div>
              <div className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{stats.avgDuration}</div>
            </div>

            {/* 예측 정확도 */}
            <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <Target className="h-4 w-4 text-red-400" />
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>예측 정확도</span>
          </div>
              <div className="text-2xl font-bold text-red-400">{stats.avgAccuracy}%</div>
            </div>
          </div>

          {/* 필터 버튼들 */}
          <div className="flex gap-2">
            <button 
              className={`px-3 py-1 rounded text-sm flex items-center gap-1 ${
                activeFilter === '전체' 
                  ? 'bg-blue-600 text-white' 
                  : theme === 'dark' 
                    ? 'bg-slate-700 text-gray-300 hover:bg-slate-600' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              onClick={() => handleFilterChange('전체')}
            >
              <BarChart3 className="h-3 w-3" />
              전체 {stats.totalPositions}
            </button>
            <button 
              className={`px-3 py-1 rounded text-sm ${
                activeFilter === '진행중' 
                  ? 'bg-blue-600 text-white' 
                  : theme === 'dark' 
                    ? 'bg-slate-700 text-gray-300 hover:bg-slate-600' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              onClick={() => handleFilterChange('진행중')}
            >
              진행중 {stats.openPositions}
            </button>
            <button 
              className={`px-3 py-1 rounded text-sm ${
                activeFilter === '완료' 
                  ? 'bg-blue-600 text-white' 
                  : theme === 'dark' 
                    ? 'bg-slate-700 text-gray-300 hover:bg-slate-600' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              onClick={() => handleFilterChange('완료')}
            >
              완료 {stats.closedPositions}
            </button>
          </div>
        </div>
      )}

      {/* 테이블 - 가로 스크롤 수정 */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full flex flex-col">
          {/* 테이블 헤더 */}
          <div className={`px-6 py-4 border-b min-w-[1000px] ${theme === 'dark' ? 'border-slate-700' : 'border-gray-200'}`}>
            <div className={`grid grid-cols-8 gap-6 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              <div className="w-32">암호화폐</div>
              <div className="w-28">진입가</div>
              <div className="w-28">종료가</div>
              <div className="w-24">수량</div>
              <div className="w-32">보유기간</div>
              <div className="w-32">손익</div>
              <div className="w-20">상태</div>
              <div className="w-24">AI</div>
            </div>
        </div>

          {/* 테이블 내용 - 단일 스크롤 컨테이너 */}
          <div className="flex-1 overflow-auto">
            <div className="min-w-[1000px]">
              {getPaginatedPositions().map((position) => {
                const { date, time } = formatDateTime(position.timestamp);
                
                    return (
                  <div key={position.id} className={`px-6 py-4 border-b hover:${theme === 'dark' ? 'bg-slate-700/50' : 'bg-gray-50'} ${theme === 'dark' ? 'border-slate-700' : 'border-gray-200'}`}>
                    <div className="grid grid-cols-8 gap-6 items-center">
                      {/* 암호화폐 */}
                      <div className="w-32">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="h-4 w-4 text-orange-400" />
                          <div>
                            <div className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{position.symbol}</div>
                            <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{date}. {time}</div>
                          </div>
                        </div>
                          </div>

                      {/* 진입가 */}
                      <div className="w-28">
                        <div className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>${formatNumber(position.entryPrice, 2)}</div>
                        <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>신뢰도 {position.confidence}%</div>
                      </div>

                      {/* 종료가 */}
                      <div className="w-28">
                        <div className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                          {position.exitPrice ? `$${formatNumber(position.exitPrice, 2)}` : '-'}
                        </div>
                        <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          {position.exitConfidence ? `신뢰도 ${position.exitConfidence}%` : ''}
                        </div>
                      </div>

                      {/* 수량 */}
                      <div className={`w-24 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{formatNumber(position.quantity, 4)}</div>

                      {/* 보유기간 */}
                      <div className="w-32">
                        <div className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{position.duration || '-'}</div>
                        <div className="flex gap-1 text-xs">
                          <span className={`${getAccuracyColor(position.oneHourAccuracy)}`}>
                            1h: {position.oneHourAccuracy ? (position.oneHourAccuracy >= 80 ? '✓' : '✗') : '-'}
                          </span>
                          <span className={`${getAccuracyColor(position.oneDayAccuracy)}`}>
                            1d: {position.oneDayAccuracy ? (position.oneDayAccuracy >= 80 ? '✓' : '✗') : '-'}
                          </span>
                        </div>
                      </div>

                      {/* 손익 - 충분한 너비 확보 */}
                      <div className="w-32">
                        {position.pnl !== undefined ? (
                          <>
                            <div className={`font-medium ${getPnlColor(position.pnl)} whitespace-nowrap`}>
                              {formatCurrency(position.pnl)}
                            </div>
                            <div className={`text-xs ${getPnlColor(position.pnl)}`}>
                              {position.pnlPercent && position.pnlPercent > 0 ? '+' : ''}{position.pnlPercent?.toFixed(2)}%
                            </div>
                          </>
                        ) : (
                          <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>-</span>
                        )}
                      </div>

                      {/* 상태 */}
                      <div className="w-20">
                        <span className={`px-2 py-1 text-xs rounded ${
                          position.status === 'OPEN' 
                            ? 'bg-green-100 text-green-800' 
                            : theme === 'dark' 
                              ? 'bg-slate-600 text-white' 
                              : 'bg-gray-100 text-gray-700'
                        }`}>
                          {position.status === 'OPEN' ? '진행중' : '완료'}
                          </span>
                      </div>

                      {/* AI */}
                      <div className="w-24">
                        <div className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{position.aiProvider}</div>
                        <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{position.aiModel}</div>
                      </div>
                    </div>
                  </div>
                    );
                  })}
            </div>
          </div>
            </div>
          </div>

          {/* 페이지네이션 */}
      <div className={`flex items-center justify-between p-4 border-t ${theme === 'dark' ? 'border-slate-700' : 'border-gray-200'}`}>
        <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
          총 {filteredPositions.length}개 중 {Math.min((currentPage - 1) * itemsPerPage + 1, filteredPositions.length)}-{Math.min(currentPage * itemsPerPage, filteredPositions.length)}개 표시
              </div>
              <div className="flex items-center gap-2">
                <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
            className={`px-3 py-1 text-sm rounded disabled:opacity-50 ${
              theme === 'dark' 
                ? 'bg-slate-700 text-gray-300 hover:bg-slate-600 disabled:hover:bg-slate-700' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:hover:bg-gray-100'
                  }`}
                >
                  이전
                </button>
          
          {/* 페이지 번호들 */}
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
            if (pageNum > totalPages) return null;
            
            return (
              <button
                key={pageNum}
                onClick={() => setCurrentPage(pageNum)}
                className={`px-3 py-1 text-sm rounded ${
                  currentPage === pageNum
                    ? 'bg-blue-600 text-white'
                    : theme === 'dark'
                      ? 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {pageNum}
              </button>
            );
          })}
          
                <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage >= totalPages}
            className={`px-3 py-1 text-sm rounded disabled:opacity-50 ${
              theme === 'dark' 
                ? 'bg-slate-700 text-gray-300 hover:bg-slate-600 disabled:hover:bg-slate-700' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:hover:bg-gray-100'
                  }`}
                >
                  다음
                </button>
              </div>
            </div>
    </div>
  );
};

export default AIPredictionPerformance; 